import React, { useState, useEffect, useMemo } from 'react'
import {
  TrendingDown,
  Calendar,
  Calculator,
  Info,
  Download,
  Settings,
  BarChart3,
  LineChart
} from 'lucide-react'
import {
  LineChart as RechartsLineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Area,
  AreaChart
} from 'recharts'
import { Button } from '../ui/Button'
import { LoadingSpinner } from '../ui/LoadingSpinner'
import { useToastHelpers } from '../../contexts/ToastContext'
import {
  DepreciationSchedule,
  DepreciationYear,
  DepreciationChartData,
  DEPRECIATION_PERIODS,
  UserTaxProfile
} from '../../types/tax-analysis'
import { PropertyResponse } from '../../services/api'

interface DepreciationScheduleVisualizationProps {
  property: PropertyResponse
  taxProfile?: UserTaxProfile | null
  loading?: boolean
  onDepreciationChange?: (schedule: DepreciationSchedule) => void
}

type ChartType = 'line' | 'bar' | 'area' | 'stacked'
type ViewMode = 'annual' | 'cumulative' | 'tax_impact' | 'comparison'

export function DepreciationScheduleVisualization({
  property,
  taxProfile,
  loading = false,
  onDepreciationChange
}: DepreciationScheduleVisualizationProps) {
  const { showSuccess, showError } = useToastHelpers()
  const [depreciationSchedule, setDepreciationSchedule] = useState<DepreciationSchedule | null>(null)
  const [chartType, setChartType] = useState<ChartType>('line')
  const [viewMode, setViewMode] = useState<ViewMode>('annual')
  const [selectedYears, setSelectedYears] = useState<number>(10) // Default to 10 years
  const [landValuePercentage, setLandValuePercentage] = useState<number>(20) // Default 20%
  const [bonusDepreciationEnabled, setBonusDepreciationEnabled] = useState<boolean>(false)
  const [isCalculating, setIsCalculating] = useState(false)

  // Calculate depreciation schedule when inputs change
  useEffect(() => {
    if (property) {
      calculateDepreciationSchedule()
    }
  }, [property, landValuePercentage, bonusDepreciationEnabled, selectedYears])

  const calculateDepreciationSchedule = async () => {
    if (!property) return

    setIsCalculating(true)
    try {
      const schedule = await generateDepreciationSchedule(
        property,
        landValuePercentage,
        bonusDepreciationEnabled
      )
      setDepreciationSchedule(schedule)

      if (onDepreciationChange) {
        onDepreciationChange(schedule)
      }
    } catch (error) {
      console.error('Depreciation calculation error:', error)
      showError('Calculation Error', 'Failed to calculate depreciation schedule')
    } finally {
      setIsCalculating(false)
    }
  }

  const generateDepreciationSchedule = async (
    prop: PropertyResponse,
    landPercent: number,
    bonusEnabled: boolean
  ): Promise<DepreciationSchedule> => {
    const purchasePrice = prop.purchase_price
    const landValue = purchasePrice * (landPercent / 100)
    const depreciableBasis = purchasePrice - landValue

    // Assume property was placed in service January 1st of current year
    const currentYear = new Date().getFullYear()
    const startDate = new Date(currentYear, 0, 1).toISOString()

    // Use straight-line depreciation for residential rental (27.5 years)
    const usefulLife = DEPRECIATION_PERIODS.residential_rental
    const annualDepreciation = depreciableBasis / usefulLife

    // Calculate bonus depreciation if enabled (100% for certain property improvements)
    const bonusDepreciationAmount = bonusEnabled ? depreciableBasis * 0.5 : 0 // Assume 50% qualifies
    const adjustedAnnualDepreciation = (depreciableBasis - bonusDepreciationAmount) / usefulLife

    const schedule: DepreciationYear[] = []
    let accumulatedDepreciation = bonusDepreciationAmount
    let remainingBasis = depreciableBasis - bonusDepreciationAmount

    for (let year = 0; year < Math.min(selectedYears, usefulLife); year++) {
      const calendarYear = (currentYear + year).toString()
      const yearlyDepreciation = year === 0 && bonusEnabled
        ? adjustedAnnualDepreciation + bonusDepreciationAmount
        : adjustedAnnualDepreciation

      accumulatedDepreciation += year === 0 ? yearlyDepreciation : adjustedAnnualDepreciation
      remainingBasis = depreciableBasis - accumulatedDepreciation

      schedule.push({
        year: year + 1,
        calendar_year: calendarYear,
        depreciation_amount: year === 0 ? yearlyDepreciation : adjustedAnnualDepreciation,
        accumulated_depreciation: accumulatedDepreciation,
        remaining_basis: Math.max(remainingBasis, 0),
        bonus_depreciation: year === 0 && bonusEnabled ? bonusDepreciationAmount : undefined
      })
    }

    return {
      property_id: prop.property_id || 'temp',
      depreciable_basis: depreciableBasis,
      depreciation_method: 'straight_line',
      useful_life_years: usefulLife,
      land_value: landValue,
      start_date: startDate,
      annual_schedule: schedule,
      total_depreciation: accumulatedDepreciation,
      remaining_basis: Math.max(depreciableBasis - accumulatedDepreciation, 0)
    }
  }

  const chartData = useMemo(() => {
    if (!depreciationSchedule || !taxProfile) return []

    return depreciationSchedule.annual_schedule.map(year => {
      const taxSavings = year.depreciation_amount * (taxProfile.federal_tax_bracket + taxProfile.state_tax_rate)

      const data: DepreciationChartData & any = {
        year: year.calendar_year,
        annual_depreciation: year.depreciation_amount,
        accumulated_depreciation: year.accumulated_depreciation,
        remaining_basis: year.remaining_basis,
        tax_savings: taxSavings,
        bonus_depreciation: year.bonus_depreciation || 0
      }

      return data
    })
  }, [depreciationSchedule, taxProfile])

  const getChartTitle = () => {
    switch (viewMode) {
      case 'annual':
        return 'Annual Depreciation Schedule'
      case 'cumulative':
        return 'Cumulative Depreciation Over Time'
      case 'tax_impact':
        return 'Tax Savings from Depreciation'
      case 'comparison':
        return 'Depreciation vs. Remaining Basis'
      default:
        return 'Depreciation Analysis'
    }
  }

  const renderChart = () => {
    if (!chartData || chartData.length === 0) return <div>No data available</div>

    const commonProps = {
      data: chartData,
      margin: { top: 5, right: 30, left: 20, bottom: 5 }
    }

    switch (chartType) {
      case 'line':
        return (
          <RechartsLineChart {...commonProps}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="year" />
            <YAxis />
            <Tooltip formatter={(value: number) => [`$${value.toLocaleString()}`, 'Amount']} />
            <Legend />
            {viewMode === 'annual' && (
              <Line
                type="monotone"
                dataKey="annual_depreciation"
                stroke="#3B82F6"
                strokeWidth={2}
                name="Annual Depreciation"
              />
            )}
            {viewMode === 'cumulative' && (
              <Line
                type="monotone"
                dataKey="accumulated_depreciation"
                stroke="#10B981"
                strokeWidth={2}
                name="Accumulated Depreciation"
              />
            )}
            {viewMode === 'tax_impact' && (
              <Line
                type="monotone"
                dataKey="tax_savings"
                stroke="#F59E0B"
                strokeWidth={2}
                name="Annual Tax Savings"
              />
            )}
            {viewMode === 'comparison' && (
              <>
                <Line
                  type="monotone"
                  dataKey="accumulated_depreciation"
                  stroke="#EF4444"
                  strokeWidth={2}
                  name="Accumulated Depreciation"
                />
                <Line
                  type="monotone"
                  dataKey="remaining_basis"
                  stroke="#8B5CF6"
                  strokeWidth={2}
                  name="Remaining Basis"
                />
              </>
            )}
          </RechartsLineChart>
        )

      case 'bar':
        return (
          <BarChart {...commonProps}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="year" />
            <YAxis />
            <Tooltip formatter={(value: number) => [`$${value.toLocaleString()}`, 'Amount']} />
            <Legend />
            {viewMode === 'annual' && (
              <Bar dataKey="annual_depreciation" fill="#3B82F6" name="Annual Depreciation" />
            )}
            {viewMode === 'cumulative' && (
              <Bar dataKey="accumulated_depreciation" fill="#10B981" name="Accumulated Depreciation" />
            )}
            {viewMode === 'tax_impact' && (
              <Bar dataKey="tax_savings" fill="#F59E0B" name="Annual Tax Savings" />
            )}
            {viewMode === 'comparison' && (
              <>
                <Bar dataKey="accumulated_depreciation" fill="#EF4444" name="Accumulated Depreciation" />
                <Bar dataKey="remaining_basis" fill="#8B5CF6" name="Remaining Basis" />
              </>
            )}
            {bonusDepreciationEnabled && (
              <Bar dataKey="bonus_depreciation" fill="#EC4899" name="Bonus Depreciation" />
            )}
          </BarChart>
        )

      case 'area':
        return (
          <AreaChart {...commonProps}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="year" />
            <YAxis />
            <Tooltip formatter={(value: number) => [`$${value.toLocaleString()}`, 'Amount']} />
            <Legend />
            {viewMode === 'cumulative' && (
              <Area
                type="monotone"
                dataKey="accumulated_depreciation"
                stackId="1"
                stroke="#10B981"
                fill="#10B981"
                fillOpacity={0.6}
                name="Accumulated Depreciation"
              />
            )}
            {viewMode === 'comparison' && (
              <>
                <Area
                  type="monotone"
                  dataKey="remaining_basis"
                  stackId="1"
                  stroke="#8B5CF6"
                  fill="#8B5CF6"
                  fillOpacity={0.6}
                  name="Remaining Basis"
                />
                <Area
                  type="monotone"
                  dataKey="accumulated_depreciation"
                  stackId="2"
                  stroke="#EF4444"
                  fill="#EF4444"
                  fillOpacity={0.6}
                  name="Accumulated Depreciation"
                />
              </>
            )}
          </AreaChart>
        )

      case 'stacked':
        return (
          <BarChart {...commonProps}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="year" />
            <YAxis />
            <Tooltip formatter={(value: number) => [`$${value.toLocaleString()}`, 'Amount']} />
            <Legend />
            <Bar dataKey="remaining_basis" stackId="a" fill="#8B5CF6" name="Remaining Basis" />
            <Bar dataKey="accumulated_depreciation" stackId="a" fill="#EF4444" name="Accumulated Depreciation" />
          </BarChart>
        )

      default:
        return <div>Unknown chart type</div>
    }
  }

  const handleExportSchedule = () => {
    if (!depreciationSchedule) {
      showError('Export Error', 'No depreciation schedule to export')
      return
    }

    try {
      const csvContent = generateDepreciationCSV(depreciationSchedule)
      const blob = new Blob([csvContent], { type: 'text/csv' })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `depreciation-schedule-${property.property_address.replace(/\s+/g, '-')}.csv`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)

      showSuccess('Export Complete', 'Depreciation schedule exported successfully')
    } catch (error) {
      console.error('Export error:', error)
      showError('Export Error', 'Failed to export depreciation schedule')
    }
  }

  if (loading || isCalculating) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-center py-8">
          <LoadingSpinner size="lg" />
          <span className="ml-3 text-gray-600">
            {isCalculating ? 'Calculating depreciation schedule...' : 'Loading...'}
          </span>
        </div>
      </div>
    )
  }

  if (!taxProfile) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="text-center py-8">
          <Calculator className="w-12 h-12 text-amber-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Tax Profile Required</h3>
          <p className="text-gray-600">
            Tax profile is needed to calculate depreciation tax impacts
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header & Controls */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <TrendingDown className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Depreciation Schedule Visualization
                </h3>
                <p className="text-sm text-gray-600">
                  Interactive analysis of property depreciation over time
                </p>
              </div>
            </div>
            <Button
              variant="secondary"
              size="sm"
              onClick={handleExportSchedule}
              className="flex items-center gap-2"
              disabled={!depreciationSchedule}
            >
              <Download className="w-4 h-4" />
              Export Schedule
            </Button>
          </div>
        </div>

        {/* Configuration Panel */}
        <div className="p-6 bg-gray-50">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Years to Display
              </label>
              <select
                value={selectedYears}
                onChange={(e) => setSelectedYears(parseInt(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500"
              >
                <option value={5}>5 Years</option>
                <option value={10}>10 Years</option>
                <option value={15}>15 Years</option>
                <option value={27}>Full Schedule (27.5 Years)</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Land Value %
              </label>
              <select
                value={landValuePercentage}
                onChange={(e) => setLandValuePercentage(parseInt(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500"
              >
                <option value={15}>15%</option>
                <option value={20}>20%</option>
                <option value={25}>25%</option>
                <option value={30}>30%</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                View Mode
              </label>
              <select
                value={viewMode}
                onChange={(e) => setViewMode(e.target.value as ViewMode)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500"
              >
                <option value="annual">Annual Depreciation</option>
                <option value="cumulative">Cumulative Depreciation</option>
                <option value="tax_impact">Tax Impact</option>
                <option value="comparison">Basis vs Depreciation</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Chart Type
              </label>
              <select
                value={chartType}
                onChange={(e) => setChartType(e.target.value as ChartType)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500"
              >
                <option value="line">Line Chart</option>
                <option value="bar">Bar Chart</option>
                <option value="area">Area Chart</option>
                <option value="stacked">Stacked Chart</option>
              </select>
            </div>
          </div>

          <div className="mt-4 flex items-center">
            <input
              type="checkbox"
              id="bonus-depreciation"
              checked={bonusDepreciationEnabled}
              onChange={(e) => setBonusDepreciationEnabled(e.target.checked)}
              className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
            />
            <label htmlFor="bonus-depreciation" className="ml-2 text-sm text-gray-700">
              Include Bonus Depreciation (if applicable)
            </label>
          </div>
        </div>
      </div>

      {/* Key Metrics Summary */}
      {depreciationSchedule && (
        <div className="bg-white rounded-lg shadow p-6">
          <h4 className="text-lg font-semibold text-gray-900 mb-4">Depreciation Summary</h4>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-blue-50 rounded-lg p-4">
              <p className="text-sm font-medium text-blue-600 uppercase tracking-wide">
                Depreciable Basis
              </p>
              <p className="text-2xl font-bold text-blue-900">
                ${depreciationSchedule.depreciable_basis.toLocaleString()}
              </p>
            </div>

            <div className="bg-green-50 rounded-lg p-4">
              <p className="text-sm font-medium text-green-600 uppercase tracking-wide">
                Annual Depreciation
              </p>
              <p className="text-2xl font-bold text-green-900">
                ${(depreciationSchedule.depreciable_basis / depreciationSchedule.useful_life_years).toLocaleString()}
              </p>
            </div>

            <div className="bg-purple-50 rounded-lg p-4">
              <p className="text-sm font-medium text-purple-600 uppercase tracking-wide">
                Total Depreciation ({selectedYears} years)
              </p>
              <p className="text-2xl font-bold text-purple-900">
                ${depreciationSchedule.annual_schedule.slice(0, selectedYears)
                  .reduce((sum, year) => sum + year.depreciation_amount, 0).toLocaleString()}
              </p>
            </div>

            <div className="bg-orange-50 rounded-lg p-4">
              <p className="text-sm font-medium text-orange-600 uppercase tracking-wide">
                Land Value (Non-Depreciable)
              </p>
              <p className="text-2xl font-bold text-orange-900">
                ${depreciationSchedule.land_value.toLocaleString()}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Chart Visualization */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-6">
          <h4 className="text-lg font-semibold text-gray-900">{getChartTitle()}</h4>
          <div className="flex items-center gap-2">
            <BarChart3 className="w-4 h-4 text-gray-500" />
            <span className="text-sm text-gray-600">
              {selectedYears} Year {chartType === 'line' ? 'Trend' : 'Analysis'}
            </span>
          </div>
        </div>

        {chartData && chartData.length > 0 ? (
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              {renderChart()}
            </ResponsiveContainer>
          </div>
        ) : (
          <div className="h-80 flex items-center justify-center text-gray-500">
            <div className="text-center">
              <LineChart className="w-12 h-12 mx-auto mb-2" />
              <p>No data available for visualization</p>
            </div>
          </div>
        )}
      </div>

      {/* Detailed Schedule Table */}
      {depreciationSchedule && (
        <div className="bg-white rounded-lg shadow p-6">
          <h4 className="text-lg font-semibold text-gray-900 mb-4">Detailed Depreciation Schedule</h4>
          <div className="overflow-x-auto">
            <table className="w-full table-auto">
              <thead>
                <tr className="bg-gray-50">
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Year</th>
                  <th className="px-4 py-3 text-right text-sm font-medium text-gray-700">Annual Depreciation</th>
                  <th className="px-4 py-3 text-right text-sm font-medium text-gray-700">Accumulated Depreciation</th>
                  <th className="px-4 py-3 text-right text-sm font-medium text-gray-700">Remaining Basis</th>
                  {taxProfile && (
                    <th className="px-4 py-3 text-right text-sm font-medium text-gray-700">Tax Savings</th>
                  )}
                  {bonusDepreciationEnabled && (
                    <th className="px-4 py-3 text-right text-sm font-medium text-gray-700">Bonus Depreciation</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {depreciationSchedule.annual_schedule.slice(0, selectedYears).map((year, index) => {
                  const taxSavings = taxProfile
                    ? year.depreciation_amount * (taxProfile.federal_tax_bracket + taxProfile.state_tax_rate)
                    : 0

                  return (
                    <tr key={year.year} className={index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                      <td className="px-4 py-3 text-sm text-gray-900">{year.calendar_year}</td>
                      <td className="px-4 py-3 text-sm text-gray-900 text-right">
                        ${year.depreciation_amount.toLocaleString()}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900 text-right">
                        ${year.accumulated_depreciation.toLocaleString()}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900 text-right">
                        ${year.remaining_basis.toLocaleString()}
                      </td>
                      {taxProfile && (
                        <td className="px-4 py-3 text-sm text-green-600 text-right font-medium">
                          ${taxSavings.toLocaleString()}
                        </td>
                      )}
                      {bonusDepreciationEnabled && (
                        <td className="px-4 py-3 text-sm text-purple-600 text-right font-medium">
                          {year.bonus_depreciation ? `$${year.bonus_depreciation.toLocaleString()}` : '-'}
                        </td>
                      )}
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Important Notes */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-6">
        <div className="flex items-start gap-3">
          <Info className="w-5 h-5 text-amber-500 mt-0.5" />
          <div>
            <h4 className="text-sm font-medium text-amber-800 mb-2">Depreciation Considerations</h4>
            <ul className="text-sm text-amber-700 space-y-1">
              <li>• Residential rental property is depreciated over 27.5 years using straight-line method</li>
              <li>• Land value is not depreciable - only the building/improvements can be depreciated</li>
              <li>• Bonus depreciation may be available for certain property improvements</li>
              <li>• Depreciation recapture taxes will apply when the property is sold</li>
              <li>• Keep detailed records of all improvements as they may qualify for separate depreciation</li>
              <li>• Consult a tax professional for specific depreciation strategies and compliance</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  )
}

// Helper function to generate CSV export
function generateDepreciationCSV(schedule: DepreciationSchedule): string {
  const headers = [
    'Year',
    'Calendar Year',
    'Annual Depreciation',
    'Accumulated Depreciation',
    'Remaining Basis',
    'Bonus Depreciation'
  ]

  const rows = schedule.annual_schedule.map(year => [
    year.year.toString(),
    year.calendar_year,
    year.depreciation_amount.toFixed(2),
    year.accumulated_depreciation.toFixed(2),
    year.remaining_basis.toFixed(2),
    year.bonus_depreciation ? year.bonus_depreciation.toFixed(2) : ''
  ])

  const csvContent = [
    'Depreciation Schedule',
    `Property ID: ${schedule.property_id}`,
    `Depreciable Basis: $${schedule.depreciable_basis.toLocaleString()}`,
    `Land Value: $${schedule.land_value.toLocaleString()}`,
    `Depreciation Method: ${schedule.depreciation_method}`,
    `Useful Life: ${schedule.useful_life_years} years`,
    '',
    headers.join(','),
    ...rows.map(row => row.join(','))
  ].join('\n')

  return csvContent
}